import { SaveableMasterRecipientDto } from '@treasury/api/channel';
import { DomainEntity } from '../shared';

export interface MasterRecipientDto {
    id: number;
    recipientName: string;
    recipientIdNumber: string | undefined;
    accountNumber: string;
    accountTypeDescription: string;
    accountTypeId: number;
    routingNumber: string;
    transactionType: string;
    transactionTypeId: number;
    defaultAmount: number;
    addenda: string | undefined;
    achMasterRecipientStatusTypeDescription: string;
    achMasterRecipientStatusTypeId: number;
    approvalCount: number;
    requiredApprovals: number;
    canApproveReject: boolean;
    canEdit: boolean;
    updatedBy: number;
}

const defaultMasterRecipientDto: MasterRecipientDto = {
    id: 0,
    recipientName: '',
    recipientIdNumber: undefined,
    accountNumber: '',
    accountTypeDescription: 'Checking',
    accountTypeId: 1,
    routingNumber: '',
    transactionType: 'CR',
    transactionTypeId: 1,
    defaultAmount: 0,
    addenda: undefined,
    achMasterRecipientStatusTypeDescription: 'Pending Approval',
    achMasterRecipientStatusTypeId: 1,
    approvalCount: 0,
    requiredApprovals: 1,
    canApproveReject: false,
    canEdit: true,
    updatedBy: 0,
};

export class MasterRecipient extends DomainEntity<MasterRecipientDto> {
    private static readonly amountFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    public static accountTypeIdFromDescription(description: string): number {
        switch (description) {
            case 'Checking':
                return 1;
            case 'Savings':
                return 2;
            case 'Loan':
                return 3;
            case 'General Ledger':
                return 7;
            default:
                return 1;
        }
    }

    public static accountTypeDescriptionFromId(id: number): string {
        switch (id) {
            case 1:
                return 'Checking';
            case 2:
                return 'Savings';
            case 3:
                return 'Loan';
            case 7:
                return 'General Ledger';
            default:
                return 'Checking';
        }
    }

    public createDefault(): this {
        return new MasterRecipient({ ...defaultMasterRecipientDto }) as this;
    }

    public get id() {
        return this.dto.id;
    }

    public get recipientName() {
        return this.dto.recipientName;
    }

    public set recipientName(value: string) {
        this.dto.recipientName = value;
    }

    public get recipientIdNumber() {
        return this.dto.recipientIdNumber;
    }

    public set recipientIdNumber(value: string | undefined) {
        this.dto.recipientIdNumber = value;
    }

    public get accountNumber() {
        return this.dto.accountNumber;
    }

    public set accountNumber(value: string) {
        this.dto.accountNumber = value;
    }

    public get accountTypeDescription() {
        return this.dto.accountTypeDescription;
    }

    public set accountTypeDescription(value: string) {
        this.dto.accountTypeDescription = value;
        this.dto.accountTypeId = MasterRecipient.accountTypeIdFromDescription(value);
    }

    public get accountTypeId() {
        return this.dto.accountTypeId;
    }

    public get routingNumber() {
        return this.dto.routingNumber;
    }

    public set routingNumber(value: string) {
        this.dto.routingNumber = value;
    }

    public get transactionType() {
        return this.dto.transactionType;
    }

    public set transactionType(value: string) {
        this.dto.transactionType = value;
        this.dto.transactionTypeId = value === 'CR' ? 1 : 2;
    }

    public get transactionTypeId() {
        return this.dto.transactionTypeId;
    }

    public get defaultAmount() {
        return this.dto.defaultAmount;
    }

    public set defaultAmount(value: number) {
        this.dto.defaultAmount = value;
    }

    public get addenda() {
        return this.dto.addenda;
    }

    public set addenda(value: string | undefined) {
        this.dto.addenda = value;
    }

    public get status() {
        return this.dto.achMasterRecipientStatusTypeDescription;
    }

    public get statusId() {
        return this.dto.achMasterRecipientStatusTypeId;
    }

    public get approvalCount() {
        return this.dto.approvalCount;
    }

    public get requiredApprovals() {
        return this.dto.requiredApprovals;
    }

    public get canApproveReject() {
        return this.dto.canApproveReject;
    }

    public get canEdit() {
        return this.dto.canEdit;
    }

    public get isPendingApproval() {
        return this.dto.achMasterRecipientStatusTypeId === 1;
    }

    public get isReady() {
        return this.dto.achMasterRecipientStatusTypeId === 2;
    }

    public get isRejected() {
        return this.dto.achMasterRecipientStatusTypeId === 3;
    }

    public get formattedAmount() {
        return MasterRecipient.amountFormatter.format(this.dto.defaultAmount);
    }

    public toSaveableDto(): SaveableMasterRecipientDto {
        return {
            id: this.dto.id || undefined,
            recipientName: this.dto.recipientName,
            recipientIdNumber: this.dto.recipientIdNumber,
            accountNumber: this.dto.accountNumber,
            accountTypeId: this.dto.accountTypeId,
            routingNumber: this.dto.routingNumber,
            transactionTypeId: this.dto.transactionTypeId,
            defaultAmount: this.dto.defaultAmount,
            addenda: this.dto.addenda,
        };
    }
}
